import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Coins, Save, BellRing } from "lucide-react";

const eur = (n) => `${Number(n || 0).toFixed(2).replace(".", ",")} €`;

/** Suivi du coût SMS : segments du mois, coût estimé, seuil d'alerte (Phase 76). */
export const TwilioCostCard = ({ onLoaded }) => {
  const [data, setData] = useState(null);
  const [form, setForm] = useState({ price_per_segment: 0.08, monthly_threshold: 50, alert_enabled: true });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data: d } = await api.get("/admin/twilio/cost");
      setData(d);
      setForm({ price_per_segment: d.settings.price_per_segment, monthly_threshold: d.settings.monthly_threshold, alert_enabled: d.settings.alert_enabled });
      onLoaded?.(d);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const save = async () => {
    setSaving(true);
    try {
      const { data: d } = await api.put("/admin/twilio/cost", { price_per_segment: Number(form.price_per_segment), monthly_threshold: Number(form.monthly_threshold), alert_enabled: form.alert_enabled });
      setData(d); onLoaded?.(d);
      toast.success("Réglages de coût enregistrés");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const testAlert = async () => {
    try { const { data: r } = await api.post("/admin/twilio/cost/check", null, { params: { force: true } }); toast.success(`Alerte envoyée aux admins (${r.emit?.queued || 0} email, ${r.emit?.push || 0} push)`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  if (!data) return null;
  const cur = data.current;
  const pct = Math.min(100, Math.round((data.ratio || 0) * 100));
  const bar = data.over_threshold ? "#E60000" : pct >= 75 ? "#F59E0B" : "#059669";

  return (
    <div className="p-5 bg-white border border-[#DEE2E6]" data-testid="tw-cost-card">
      <div className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Coins className="h-5 w-5 text-[#F59E0B]" />Coût SMS du mois ({cur.month})</div>
      <div className="text-[11px] text-[#495057] mb-3">Estimation = segments de 160 caractères × prix unitaire. Une alerte email + push est envoyée aux admins une fois par mois dès que le seuil est atteint.</div>
      <div className="grid grid-cols-3 gap-3">
        <div><div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">SMS envoyés</div><div className="font-display font-black text-2xl" data-testid="tw-cost-sms">{cur.sms_count}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Segments</div><div className="font-display font-black text-2xl" data-testid="tw-cost-segments">{cur.segments}</div></div>
        <div><div className="text-[10px] uppercase tracking-wider font-bold text-[#868E96]">Coût estimé</div><div className="font-display font-black text-2xl" style={{ color: bar }} data-testid="tw-cost-estimated">{eur(cur.estimated_cost)}</div></div>
      </div>
      <div className="mt-3 h-2 bg-[#F1F3F5]" aria-hidden="true"><div className="h-2 transition-all" style={{ width: `${pct}%`, background: bar }} /></div>
      <div className="mt-1 flex justify-between text-[10px] text-[#868E96]"><span data-testid="tw-cost-ratio">{pct} % du seuil ({eur(data.settings.monthly_threshold)})</span><span>Mois précédent : {eur(data.previous.estimated_cost)} ({data.previous.sms_count} SMS)</span></div>
      {cur.by_kind.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="tw-cost-by-kind">
          {cur.by_kind.slice(0, 6).map((k) => <span key={k.kind} className="text-[10px] border border-[#DEE2E6] px-1.5 py-0.5 bg-[#F8F9FA]">{k.kind} · {k.segments} seg · {eur(k.cost)}</span>)}
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 gap-3 items-end">
        <div><Label className="text-[10px] uppercase tracking-wider">Prix / segment (€ HT)</Label><Input type="number" step="0.001" min="0" value={form.price_per_segment} onChange={(e) => setForm({ ...form, price_per_segment: e.target.value })} className="h-9" data-testid="tw-cost-price" /></div>
        <div><Label className="text-[10px] uppercase tracking-wider">Seuil mensuel (€)</Label><Input type="number" step="1" min="0" value={form.monthly_threshold} onChange={(e) => setForm({ ...form, monthly_threshold: e.target.value })} className="h-9" data-testid="tw-cost-threshold" /></div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-xs"><Switch checked={form.alert_enabled} onCheckedChange={(v) => setForm({ ...form, alert_enabled: v })} data-testid="tw-cost-alert-toggle" />Alerte au franchissement</label>
        <Button size="sm" onClick={save} disabled={saving} className="h-8 bg-[#002FA7] text-white" data-testid="tw-cost-save"><Save className="h-3.5 w-3.5 mr-1" />{saving ? "…" : "Enregistrer"}</Button>
        <Button size="sm" variant="outline" onClick={testAlert} className="h-8" data-testid="tw-cost-test"><BellRing className="h-3.5 w-3.5 mr-1" />Tester l'alerte</Button>
        {data.alerted_this_month && <span className="text-[10px] text-[#F59E0B] font-bold uppercase" data-testid="tw-cost-alerted">Alerte déjà envoyée ce mois</span>}
      </div>
    </div>
  );
};
